import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Button,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  Divider,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  Timer,
  CheckCircle,
  Cancel,
  NavigateBefore,
  NavigateNext,
  Info,
  ViewWeek,
} from '@mui/icons-material';
import AnswerSheetDock from '../components/AnswerSheetDock';
import AnswerSheet from '../components/AnswerSheet';

const TakeSpecificExam = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [viewMode, setViewMode] = useState('dock'); // 'dock' o 'side-by-side'
  const [showProgressAlert, setShowProgressAlert] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [savedProgressData, setSavedProgressData] = useState(null);

  // Cargar progreso guardado al iniciar
  useEffect(() => {
    // SIEMPRE cargar los datos del examen primero
    fetchExamData();
    
    // Después verificar si hay progreso guardado
    const savedProgress = localStorage.getItem(`specific_exam_progress_${examId}`);
    if (savedProgress) {
      const progressData = JSON.parse(savedProgress);
      setSavedProgressData(progressData);
      setShowResumeDialog(true); // Mostrar diálogo para decidir
    }
  }, [examId]);

  // Función para continuar con progreso guardado
  const handleResumeProgress = () => {
    if (savedProgressData) {
      const { savedAnswers, savedCurrentQuestion, savedTimeLeft, savedShowInstructions } = savedProgressData;
      setAnswers(savedAnswers || {});
      setCurrentQuestion(savedCurrentQuestion || 0);
      if (savedTimeLeft !== null) setTimeLeft(savedTimeLeft);
      setShowInstructions(savedShowInstructions !== false);
      
      // Mostrar alerta de progreso restaurado
      const answeredCount = Object.keys(savedAnswers || {}).length;
      setProgressMessage(`Progreso anterior restaurado: ${answeredCount} pregunta${answeredCount !== 1 ? 's' : ''} respondida${answeredCount !== 1 ? 's' : ''}`);
      setShowProgressAlert(true);
    }
    setShowResumeDialog(false);
  };

  // Función para iniciar nuevo examen
  const handleStartNew = () => {
    localStorage.removeItem(`specific_exam_progress_${examId}`);
    setSavedProgressData(null);
    setAnswers({});
    setCurrentQuestion(0);
    setShowInstructions(true);
    setShowResumeDialog(false);
  };

  // Guardar progreso automáticamente
  useEffect(() => {
    if (!loading && !showResults && timeLeft !== null) {
      const progressData = {
        savedAnswers: answers,
        savedCurrentQuestion: currentQuestion,
        savedTimeLeft: timeLeft,
        savedShowInstructions: showInstructions,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(`specific_exam_progress_${examId}`, JSON.stringify(progressData));
    }
  }, [answers, currentQuestion, timeLeft, showInstructions, examId, loading, showResults]);

  useEffect(() => {
    if (loading || showResults || showInstructions || timeLeft === null) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [loading, showResults, showInstructions, timeLeft]);

  const fetchExamData = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Cargando examen con ID:', examId);
      console.log('API URL:', process.env.REACT_APP_API_URL);
      
      // Obtener detalles del examen
      const examResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/specific-exams/${examId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Examen cargado:', examResponse.data);
      setExam(examResponse.data);
      setTimeLeft(examResponse.data.time_limit * 60); // Convertir minutos a segundos

      // Obtener preguntas del examen
      const questionsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/specific-exams/${examId}/questions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Respuesta completa:', questionsResponse.data);
      
      // El backend devuelve { exam: {...}, questions: [...] }
      const questionsData = questionsResponse.data.questions || questionsResponse.data;
      console.log('Preguntas cargadas:', questionsData);
      console.log('Número de preguntas:', questionsData.length);
      
      // Parsear options si vienen como string
      const parsedQuestions = questionsData.map(q => ({
        ...q,
        options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options
      }));
      
      setQuestions(parsedQuestions);
    } catch (error) {
      console.error('Error completo:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      alert(`Error al cargar el examen: ${error.response?.data?.message || error.message}`);
      navigate('/exams');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (answer) => {
    setAnswers({
      ...answers,
      [currentQuestion]: answer,
    });
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Calcular respuestas correctas
      let correctCount = 0;
      const detailedResults = questions.map((q, index) => {
        const userAnswer = answers[index];
        const correctAnswer = ['A', 'B', 'C', 'D'][q.options.indexOf(q.correct_answer)];
        const isCorrect = userAnswer === correctAnswer;
        
        if (isCorrect) correctCount++;
        
        return {
          question: q.question,
          userAnswer,
          correctAnswer,
          isCorrect,
          explanation: q.explanation,
        };
      });

      const score = Math.round((correctCount / questions.length) * 100);
      const timeSpent = (exam.time_limit * 60) - timeLeft;

      setResults({
        score,
        correct: correctCount,
        incorrect: questions.length - correctCount,
        total: questions.length,
        timeSpent,
        detailedResults,
        passed: score >= (exam.passing_score || 60),
      });

      // Guardar resultado en el backend
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/specific-exams/${examId}/submit`,
        {
          answers,
          score,
          timeSpent,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowResults(true);
      // Limpiar progreso guardado al completar
      localStorage.removeItem(`specific_exam_progress_${examId}`);
    } catch (error) {
      console.error('Error submitting exam:', error);
      alert('Error al enviar el examen');
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const startExam = () => {
    setShowInstructions(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!exam || questions.length === 0) {
    return (
      <Container>
        <Box sx={{ mt: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No se pudo cargar el examen
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
              Debug: exam={exam ? 'existe' : 'null'}, questions.length={questions.length}
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/exams')}
            >
              Volver a Exámenes
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Validar que el índice de pregunta actual sea válido
  if (currentQuestion >= questions.length) {
    return (
      <Container>
        <Box sx={{ mt: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Error: Índice de pregunta inválido
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/exams')}
            >
              Volver a Exámenes
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const question = questions[currentQuestion];
  const optionLabels = ['A', 'B', 'C', 'D'];

  // Pantalla de instrucciones
  if (showInstructions) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
        <Container maxWidth="md">
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#1a237e' }}>
                {exam.title}
              </Typography>
              <Chip label={exam.area} color="primary" sx={{ mr: 1 }} />
              <Chip label={exam.difficulty_level} color="secondary" />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info color="primary" /> Instrucciones
              </Typography>
              <Typography variant="body1" paragraph>
                {exam.instructions || 'Lee cuidadosamente cada pregunta y selecciona la respuesta correcta.'}
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                  <Typography variant="h4" color="primary">{questions.length}</Typography>
                  <Typography variant="caption">Preguntas</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                  <Typography variant="h4" color="secondary">{exam.time_limit}</Typography>
                  <Typography variant="caption">Minutos</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e5f5' }}>
                  <Typography variant="h4" sx={{ color: '#7b1fa2' }}>{exam.passing_score || 60}%</Typography>
                  <Typography variant="caption">Puntaje mínimo</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                  <Typography variant="h4" sx={{ color: '#388e3c' }}>{exam.difficulty_level}</Typography>
                  <Typography variant="caption">Dificultad</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                • El cronómetro comenzará cuando presiones "Iniciar Examen"<br />
                • Marca tus respuestas en la hoja de respuestas (óvalos A, B, C, D)<br />
                • Puedes navegar entre preguntas libremente<br />
                • El examen se enviará automáticamente al terminar el tiempo
              </Typography>
            </Alert>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={startExam}
                sx={{ px: 6, py: 1.5, fontWeight: 'bold', fontSize: '1.1rem' }}
              >
                Iniciar Examen
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh', pb: { xs: 8, md: 0 } }}>
      {/* Dialog de advertencia de examen en progreso */}
      <Dialog
        open={showResumeDialog}
        onClose={() => {}} // No permitir cerrar sin elegir
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Timer sx={{ fontSize: 32 }} />
            <Typography variant="h6">Examen en Progreso</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Tienes un examen <strong>{exam?.title || 'este examen'}</strong> que no has terminado.
          </Alert>
          {savedProgressData && (
            <Box sx={{ pl: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                📊 <strong>Preguntas respondidas:</strong> {Object.keys(savedProgressData.savedAnswers || {}).length}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                ⏱️ <strong>Tiempo restante:</strong> {Math.floor((savedProgressData.savedTimeLeft || 0) / 60)} minutos
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                💾 Guardado el: {savedProgressData.savedAt ? new Date(savedProgressData.savedAt).toLocaleString('es-ES') : 'Desconocido'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={handleStartNew}
            variant="outlined"
            color="error"
            size="large"
          >
            Iniciar Nuevo Examen
          </Button>
          <Button
            onClick={handleResumeProgress}
            variant="contained"
            color="primary"
            size="large"
            startIcon={<CheckCircle />}
          >
            Continuar donde lo dejé
          </Button>
        </DialogActions>
      </Dialog>

      {/* Barra de información del examen (no AppBar para evitar duplicados) */}
      <Paper elevation={3} sx={{ bgcolor: '#1a237e', color: 'white', borderRadius: 0 }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => {
              if (window.confirm('¿Estás seguro de que deseas salir del examen?')) {
                navigate('/exams');
              }
            }}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {exam.title}
          </Typography>
          
          {/* Botón de cambio de modo */}
          <IconButton
            color="inherit"
            onClick={() => setViewMode(viewMode === 'dock' ? 'side-by-side' : 'dock')}
            sx={{ mr: 2 }}
            title={viewMode === 'dock' ? 'Cambiar a vista lado a lado' : 'Cambiar a vista dock'}
          >
            <ViewWeek />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Timer />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: timeLeft < 300 ? '#ff5252' : 'white' // Rojo si quedan menos de 5 min
              }}
            >
              {formatTime(timeLeft)}
            </Typography>
          </Box>
        </Toolbar>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 6 }} />
      </Paper>

      {/* Dock flotante (solo en modo dock) */}
      {viewMode === 'dock' && (
        <AnswerSheetDock
          totalQuestions={questions.length}
          answers={answers}
          onAnswerSelect={(index, answer) => setAnswers({ ...answers, [index]: answer })}
          currentQuestion={currentQuestion}
          onQuestionNavigate={setCurrentQuestion}
        />
      )}

      <Container 
        maxWidth={viewMode === 'dock' ? 'md' : 'lg'} 
        sx={{ mt: 3, mb: 4, pr: { xs: 2, md: viewMode === 'dock' ? 2 : 2 } }}
      >
        {viewMode === 'side-by-side' ? (
          // Vista lado a lado (modo original)
          <Grid container spacing={3}>
            {/* Columna izquierda: Pregunta */}
            <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3, bgcolor: 'white' }}>
              {/* Header de pregunta */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip 
                  label={`Pregunta ${currentQuestion + 1} de ${questions.length}`} 
                  color="primary" 
                  size="medium"
                />
                <Chip 
                  label={question.topic || exam.area} 
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Contexto de la pregunta (si existe) */}
              {(question?.context_text || question?.context_image) && (
                <Box sx={{ mb: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: 4, borderColor: 'primary.main' }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                    📖 CONTEXTO
                  </Typography>
                  
                  {question.context_image && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <img 
                        src={question.context_image} 
                        alt="Contexto"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '300px',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                      />
                    </Box>
                  )}
                  
                  {question.context_text && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6,
                        color: '#333'
                      }}
                    >
                      {question.context_text}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Texto de la pregunta */}
              <Box sx={{ mb: 4, minHeight: '120px' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 500,
                    lineHeight: 1.6,
                    color: '#333'
                  }}
                >
                  {question?.question || 'Pregunta no disponible'}
                </Typography>
                
                {/* Imagen de la pregunta si existe */}
                {question?.image_url && (
                  <Box sx={{ mt: 3, mb: 2, textAlign: 'center' }}>
                    <img 
                      src={question.image_url} 
                      alt="Imagen de la pregunta"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '400px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* Opciones con letras A, B, C, D */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Seleccione la respuesta correcta:
                </Typography>
                
                {question?.options?.map((option, index) => {
                  const letter = optionLabels[index];
                  const isSelected = answers[currentQuestion] === letter;
                  
                  return (
                    <Paper
                      key={index}
                      elevation={isSelected ? 3 : 0}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: 2,
                        borderColor: isSelected ? 'primary.main' : 'grey.300',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        bgcolor: isSelected ? 'primary.50' : 'white',
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateX(4px)',
                        },
                      }}
                      onClick={() => handleAnswerChange(letter)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            border: 2,
                            borderColor: isSelected ? 'primary.main' : 'grey.400',
                            bgcolor: isSelected ? 'primary.main' : 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Typography 
                            variant="body1" 
                            fontWeight="bold"
                            color={isSelected ? 'white' : 'text.primary'}
                          >
                            {letter}
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ flex: 1 }}>
                          {option}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>

              {/* Navegación */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="outlined"
                  size="large"
                  disabled={currentQuestion === 0}
                  onClick={() => setCurrentQuestion(currentQuestion - 1)}
                  startIcon={<NavigateBefore />}
                >
                  Anterior
                </Button>
                <Box>
                  {currentQuestion === questions.length - 1 ? (
                    <Button
                      variant="contained"
                      size="large"
                      color="success"
                      onClick={handleSubmit}
                      disabled={Object.keys(answers).length !== questions.length}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Finalizar Examen
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setCurrentQuestion(currentQuestion + 1)}
                      endIcon={<NavigateNext />}
                    >
                      Siguiente
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Columna derecha: Hoja de respuestas estilo ICFES */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 2, 
                position: 'sticky', 
                top: 16,
                bgcolor: 'white',
                border: 2,
                borderColor: '#333'
              }}
            >
              <Box sx={{ mb: 2, textAlign: 'center', bgcolor: '#1a237e', color: 'white', py: 1, borderRadius: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                  HOJA DE RESPUESTAS
                </Typography>
                <Typography variant="caption">
                  Marca solo una opción
                </Typography>
              </Box>

              {/* Grid de respuestas */}
              <Box sx={{ maxHeight: '70vh', overflowY: 'auto', pr: 1 }}>
                {questions.map((q, index) => {
                  const isActive = currentQuestion === index;
                  const hasAnswer = answers[index] !== undefined;
                  
                  return (
                    <Box
                      key={index}
                      onClick={() => setCurrentQuestion(index)}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        bgcolor: isActive ? '#e3f2fd' : hasAnswer ? '#f5f5f5' : 'white',
                        border: 2,
                        borderColor: isActive ? '#1976d2' : hasAnswer ? '#4caf50' : '#ddd',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: '#f5f5f5',
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <AnswerSheet
                        questionNumber={index + 1}
                        selectedAnswer={answers[index]}
                        onAnswerSelect={(answer) => {
                          setAnswers({ ...answers, [index]: answer });
                          setCurrentQuestion(index);
                        }}
                        disabled={false}
                      />
                    </Box>
                  );
                })}
              </Box>

              {/* Leyenda */}
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: 2, borderColor: '#ddd', bgcolor: 'white' }} />
                    <Typography variant="caption">Sin marcar</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#333' }} />
                    <Typography variant="caption">Marcada</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#1976d2' }} />
                    <Typography variant="caption">Actual</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Resumen */}
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" align="center">
                  <strong>{Object.keys(answers).length}</strong> de <strong>{questions.length}</strong> respondidas
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        ) : (
          // Vista dock (solo pregunta centrada)
          <Paper elevation={3} sx={{ p: 3, bgcolor: 'white', maxWidth: '800px', mx: 'auto' }}>
            {/* Header de pregunta */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Chip 
                label={`Pregunta ${currentQuestion + 1} de ${questions.length}`} 
                color="primary" 
                size="medium"
              />
              <Chip 
                label={question?.topic || exam?.area || 'General'} 
                variant="outlined"
                size="small"
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Contexto de la pregunta (si existe) */}
            {(question?.context_text || question?.context_image) && (
              <Box sx={{ mb: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: 4, borderColor: 'primary.main' }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                  📖 CONTEXTO
                </Typography>
                
                {question?.context_image && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <img 
                      src={question.context_image} 
                      alt="Contexto"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '300px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                  </Box>
                )}
                
                {question?.context_text && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                      color: '#333'
                    }}
                  >
                    {question.context_text}
                  </Typography>
                )}
              </Box>
            )}

            {/* Texto de la pregunta */}
            <Box sx={{ mb: 4, minHeight: '100px' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 500,
                  lineHeight: 1.6,
                  color: '#333'
                }}
              >
                {question?.question || 'Pregunta no disponible'}
              </Typography>
              
              {/* Imagen de la pregunta si existe */}
              {question?.image_url && (
                <Box sx={{ mt: 3, mb: 2, textAlign: 'center' }}>
                  <img 
                    src={question.image_url} 
                    alt="Imagen de la pregunta"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '400px',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Opciones con letras A, B, C, D */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Seleccione la respuesta correcta:
              </Typography>
              
              {question?.options?.map((option, index) => {
                const letter = optionLabels[index];
                const isSelected = answers[currentQuestion] === letter;
                
                return (
                  <Paper
                    key={index}
                    elevation={isSelected ? 3 : 0}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: 2,
                      borderColor: isSelected ? 'primary.main' : 'grey.300',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      bgcolor: isSelected ? 'primary.50' : 'white',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateX(4px)',
                      },
                    }}
                    onClick={() => handleAnswerChange(letter)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          border: 2,
                          borderColor: isSelected ? 'primary.main' : 'grey.400',
                          bgcolor: isSelected ? 'primary.main' : 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Typography 
                          variant="body1" 
                          fontWeight="bold"
                          color={isSelected ? 'white' : 'text.primary'}
                        >
                          {letter}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        {option}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })}
            </Box>

            {/* Navegación */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
              <Button
                variant="outlined"
                size="large"
                disabled={currentQuestion === 0}
                onClick={() => setCurrentQuestion(currentQuestion - 1)}
                startIcon={<NavigateBefore />}
              >
                Anterior
              </Button>
              <Box>
                {currentQuestion === questions.length - 1 ? (
                  <Button
                    variant="contained"
                    size="large"
                    color="success"
                    onClick={handleSubmit}
                    disabled={Object.keys(answers).length !== questions.length}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Finalizar Examen
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setCurrentQuestion(currentQuestion + 1)}
                    endIcon={<NavigateNext />}
                  >
                    Siguiente
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Container>

      {/* Results Dialog */}
      <Dialog open={showResults} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: results?.passed ? '#4caf50' : '#f44336', color: 'white', textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {results?.passed ? '¡Felicitaciones! 🎉' : '¡Sigue Practicando! 💪'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {results && (
            <Box>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {results.score}%
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {results.correct} de {results.total} correctas
                </Typography>
                <Chip 
                  label={results.passed ? 'APROBADO' : 'NO APROBADO'} 
                  color={results.passed ? 'success' : 'error'}
                  size="large"
                  sx={{ mt: 1, fontWeight: 'bold' }}
                />
              </Box>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                    <CheckCircle color="success" sx={{ fontSize: 40 }} />
                    <Typography variant="h6">{results.correct}</Typography>
                    <Typography variant="caption">Correctas</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
                    <Cancel color="error" sx={{ fontSize: 40 }} />
                    <Typography variant="h6">{results.incorrect}</Typography>
                    <Typography variant="caption">Incorrectas</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                    <Timer color="primary" sx={{ fontSize: 40 }} />
                    <Typography variant="h6">{formatTime(results.timeSpent)}</Typography>
                    <Typography variant="caption">Tiempo</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Puntaje requerido: {exam.passing_score || 60}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={results.score} 
                sx={{ height: 10, borderRadius: 5 }}
                color={results.passed ? 'success' : 'error'}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
          <Button onClick={() => navigate('/exams')} variant="outlined" size="large">
            Ver Más Exámenes
          </Button>
          <Button onClick={() => navigate('/ranking')} variant="contained" size="large">
            Ver Ranking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mostrar progreso restaurado */}
      <Snackbar
        open={showProgressAlert}
        autoHideDuration={5000}
        onClose={() => setShowProgressAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowProgressAlert(false)} 
          severity="info" 
          sx={{ width: '100%' }}
        >
          {progressMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TakeSpecificExam;

